import React from 'react';
import { Link } from 'react-router-dom';
import { LOGOS, BRAND, getLogoForTheme } from '@/config/brand';
import { useTheme } from '@/context/ThemeContext';

/**
 * PublicFooter — shared footer for public pages.
 *
 * Props:
 *  - columns: [{ title, links: [{ href, label, external, testId }] }]
 *  - legal: [{ href, label, external, testId }]  (small bottom row)
 *  - tagline: short string under logo
 *  - compact: boolean  — slim footer for task-focused pages (login/register)
 *  - forceLogo: optional logo src override (for acq-variant pages)
 */
export default function PublicFooter({
  columns = [],
  legal = [],
  tagline,
  compact = false,
  forceLogo,
}) {
  const { theme } = useTheme();
  const logoSrc = forceLogo || getLogoForTheme(theme) || LOGOS.DARK_BG;

  // Compact variant — thin strip with logo + copyright + legal. No columns.
  if (compact) {
    return (
      <footer className="border-t border-border bg-background" data-testid="public-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={logoSrc} alt="Hungry Canadian" className="h-6 opacity-70" />
            <span className="text-xs text-muted-foreground">
              &copy; {BRAND.copyrightYear} {BRAND.name}
            </span>
          </div>
          {legal.length > 0 && (
            <ul className="flex flex-wrap gap-x-5 gap-y-2">
              {legal.map((l) => (
                <li key={l.href + l.label}>
                  {l.external ? (
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={l.testId}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      to={l.href}
                      data-testid={l.testId}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-border bg-background" data-testid="public-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="grid gap-10 md:grid-cols-[1.2fr_2fr]">
          {/* Brand column */}
          <div>
            <img src={logoSrc} alt="Hungry Canadian" className="h-8 mb-4 opacity-80" />
            {tagline && (
              <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                {tagline}
              </p>
            )}
          </div>

          {/* Link columns */}
          {columns.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
              {columns.map((col) => (
                <div key={col.title}>
                  <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3">
                    {col.title}
                  </p>
                  <ul className="space-y-2">
                    {col.links.map((l) => (
                      <li key={l.href + l.label}>
                        {l.external ? (
                          <a
                            href={l.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={l.testId}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {l.label}
                          </a>
                        ) : (
                          <Link
                            to={l.href}
                            data-testid={l.testId}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {l.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom row */}
        <div className="mt-10 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            &copy; {BRAND.copyrightYear} {BRAND.name}. All rights reserved.
          </p>
          {legal.length > 0 && (
            <ul className="flex flex-wrap gap-x-5 gap-y-2">
              {legal.map((l) => (
                <li key={l.href + l.label}>
                  {l.external ? (
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={l.testId}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      to={l.href}
                      data-testid={l.testId}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </footer>
  );
}
